import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import type { ErrorBoundaryState } from '@/types';

export interface ErrorBoundaryFallbackArgs {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  reset: () => void;
}

export interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * Renders the replacement UI once a descendant has thrown.
   * Receives the error and a `reset` callback that clears the boundary.
   */
  fallback: (args: ErrorBoundaryFallbackArgs) => ReactNode;
  /**
   * When this value changes while an error is displayed, the boundary clears
   * itself. Pass the current route so that navigating away recovers the app
   * without a manual browser reload.
   */
  resetKey?: string;
  /**
   * Optional reporting hook, called in addition to the console. The console
   * report is unconditional - a boundary that hides the failure from the
   * console trades a blank page for a silent one.
   */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

/**
 * React error boundary.
 *
 * A render error in any descendant is caught here and replaced with
 * `fallback`, instead of unmounting the tree and leaving a blank page.
 *
 * Boundaries are expected to nest: the widget-level boundary in
 * `dashboard/core/WidgetRegistry` keeps a single broken widget from taking
 * down the page around it, and this one keeps a broken page from taking down
 * the application. The innermost boundary wins, so adding this one does not
 * change existing widget behaviour.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false, error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, errorInfo: null };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Never swallow: the failure has to stay diagnosable from the browser
    // console, which is often the only telemetry available on a deployed
    // build. Application Insights is registered but inert, so this is it.
    console.error('[SupportDesk] Unhandled render error:', error, errorInfo);

    this.setState({ errorInfo });

    try {
      this.props.onError?.(error, errorInfo);
    } catch (reportingError) {
      // A failing reporter must not replace the original error.
      console.error('[SupportDesk] Error reporter threw:', reportingError);
    }
  }

  override componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Recover on navigation. Without this the fallback would survive a route
    // change and the user would be stuck on it until they reloaded.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.reset();
    }
  }

  reset = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback({
        error: this.state.error,
        errorInfo: this.state.errorInfo,
        reset: this.reset,
      });
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
